<template>
    <p-modal name="rentalStatusModal"
        label="Rental Status"
        ref="rentalStatusModal"
        size="lg"
        @ok.prevent="createRentalStatusReport"
        :disable-button="disabled"
    >
        <template v-slot:default>
            <p-form ref="rentalPeriodForm">
            <div class="container-fluid content-wrapper">
                <b-row>
                    <b-col>
                        <p-advanced-select label="Rental Period"
                            :options="rentalPeriodOptions"
                            text-field="text"
                            value-field="value"
                            v-model="reportParameters.rentalPeriodId"
                            mode="lazy"
                            rules="required"
                        />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <p-select label="Rental Source"
                            :options="rentalSourceOptions"
                            v-model="reportParameters.rentalSourceId"
                            mode="lazy"
                            rules="required"
                            selected="selected"
                            :disabled="true"
                        />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <p-card title="Rental Status">
                            <p-checkbox size="md"
                                label="Outstanding"
                                v-model="reportParameters.includeOutstanding"
                            />
                            <p-checkbox size="md"
                                label="Rental Not Returned"
                                v-model="reportParameters.includeRentalNotReturned"
                            />
                            <p-checkbox size="md"
                                label="Returned"
                                v-model="reportParameters.includeReturned"
                            />
                            <p-checkbox size="md"
                                label="Refunded"
                                v-model="reportParameters.includeRefunded"
                            />
                            <p-checkbox size="md"
                                label="Billed"
                                v-model="reportParameters.includeBilled" />                            
                        </p-card>
                    </b-col>
                </b-row>
                <UserReportTemplate 
                    :user-report-template-options="userReportTemplateOptions"
                    v-model:selected-user-report-template-id="selectedUserReportTemplateId"
                    :selected-user-report-template="selectedUserReportTemplate"
                    v-model:template-name="templateName"
                    @selected-object="applyUserReportTemplate($event)" />
            </div>
        </p-form>
        </template>
        <template v-slot:modal-footer="{ ok, cancel }">
            <b-row>
                <b-col>
                    <p-button
                        variant="outline-primary"
                        :is-busy="isBusy"
                        @click="cancel"
                        >Cancel</p-button
                    >
                    <b-overlay :show="isBusy"
                                rounded
                                opacity="0.5"
                                spinner-small
                                spinner-variant="primary"
                                class="d-inline-block ms-2">
                        <b-dropdown text="Export" 
                                    variant="outline-primary">
                            <b-dropdown-item @click="ok">CSV</b-dropdown-item>
                        </b-dropdown>
                    </b-overlay>
                </b-col>
            </b-row>
        </template>
    </p-modal>
</template>

<script>

import axios from 'axios';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import ReportTemplates from '@/components/mixins/ReportTemplates.js';
import UserReportTemplate from '@/components/UserReportTemplate.vue';

export default {
    mixins: [ReportTemplates],
    components: { UserReportTemplate },
    data() {
        return {
            isBusy: false,
            // Identifies which report saved templates belong to; must match the report name in Reports.vue.
            reportName: 'Rental Status',
            disabled: true,
            rentalPeriodOptions: null,
            rentalSourceOptions: [
                { text: 'Store Managed', value: 1 }
            ],
            reportParameters: {
                rentalPeriodId: null,
                rentalSourceId: 1,
                includeOutstanding: true,
                includeRentalNotReturned: false,
                includeReturned: false,
                includeRefunded: false,
                includeBilled: false,
                locationId: null
            }
        }
    },
    methods: {
        async show() {
            this.reportParameters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
            this.rentalPeriodOptions = (await selectListOptionsDataContext
                .getStoreDropdownData('rentalPeriods'))
                .filter(x => x.locationId == this.reportParameters.locationId);

            await this.loadUserReportTemplates();
            this.$refs.rentalStatusModal.show();
        },
        // ReportTemplates hook: return false when anything in the saved template can no longer be
        // selected, and the mixin warns and loads nothing. rentalPeriodOptions is already filtered
        // to the current location, so checking the saved period against it also rejects a template
        // saved at a different location — which keeps the restored locationId consistent with the
        // restored period, since a rental period belongs to exactly one location.
        async validateReportParameters(savedParameters) {
            if (!this.rentalSourceOptions.some(rentalSourceOption => rentalSourceOption.value === savedParameters.rentalSourceId)) {
                return false;
            }

            return (this.rentalPeriodOptions ?? []).some(rentalPeriodOption =>
                rentalPeriodOption.value == savedParameters.rentalPeriodId);
        },
        async createRentalStatusReport() {
            if (!(await this.$refs.rentalPeriodForm.validate())) {
                return;
            }

            if (!(await this.saveUserReportTemplate())) {
                return;
            }

            await axios
                .post('/rentals/rentalstatusreport', this.reportParameters, {
                    responseType: 'blob'
                })
                .then(result => {
                    downloadFileToBrowser(
                        result.data,
                        'Rental Status Report.csv',
                        'application/csv'
                    );
                    this.$appSuccess(`Rental Status Report generated successfully.`)
                    this.$refs.rentalStatusModal.hide();
                });
        }
    }
}
</script>
